import React, { useState } from "react";
import Client from "../../models/client";
import { useSWRConfig } from "swr";
import { useForm } from "@mantine/form";
import { FETCH_KEYS, updateLINEMessage, updateMailSubject } from "../api";
import { notifications } from "@mantine/notifications";
import { Box, Button, Group, Text, Textarea, TextInput, ThemeIcon, Title } from "@mantine/core";
import { WrenchIcon } from "@phosphor-icons/react";

interface Props {
  client: Client
}

interface FormValues {
  mailSubject: string
  lineMessage: string
}

const validateRequired255 = (value: string) => {
  if (!value) return '必須です'
  if (value.length > 255) return '255文字以下で入力してください'
  return undefined
}

const InitialMessageContainer: React.FC<Props> = ({ client }) => {
  const { mutate } = useSWRConfig()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: {
      mailSubject: client.mail_subject ?? '',
      lineMessage: client.line_message ?? '',
    },
    validate: {
      mailSubject: validateRequired255,
      lineMessage: validateRequired255,
    },
  })

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    const tasks: {
      field: keyof FormValues
      label: string
      value: string
      run: () => Promise<void>
    }[] = []

    if (form.isDirty('mailSubject')) {
      tasks.push({
        field: 'mailSubject',
        label: 'メール件名',
        value: values.mailSubject,
        run: () => updateMailSubject(client.id, values.mailSubject),
      })
    }
    if (form.isDirty('lineMessage')) {
      tasks.push({
        field: 'lineMessage',
        label: '初期メッセージ',
        value: values.lineMessage,
        run: () => updateLINEMessage(client.id, values.lineMessage),
      })
    }

    if (tasks.length === 0) {
      setSubmitting(false)
      return
    }

    const results = await Promise.allSettled(tasks.map((t) => t.run()))

    const nextInitial: FormValues = { ...form.getValues() }

    let anySuccess = false
    results.forEach((r, i) => {
      const t = tasks[i]
      if (r.status === 'fulfilled') {
        notifications.show({ message: `${t.label}を保存しました`, color: 'teal' })
        nextInitial[t.field] = t.value
        anySuccess = true
      } else {
        notifications.show({ message: `${t.label}の保存に失敗しました`, color: 'red' })
        nextInitial[t.field] = form.getInitialValues()[t.field]
      }
    })

    if (anySuccess) {
      form.resetDirty(nextInitial)
      mutate(FETCH_KEYS.fetchClient(client.id))
    }

    setSubmitting(false)
  }

  const isDirty = form.isDirty()

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <WrenchIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          紹介メッセージ設定
        </Title>
      </Group>
      <Text fz={14} c="#000000CC" mb={40}>
        紹介時に、LINEやメールでゲストへ送信されるメッセージを初期設定します。<br />
        inviteフォームでデフォルト設定されている場合や、「おすすめメッセージ」機能を設定している場合は、そちらの内容が優先されます。
      </Text>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box mb={32}>
          <Title order={2} size={15} fw={600} mb={16}>
            メール件名
          </Title>
          <TextInput w={520} {...form.getInputProps('mailSubject')} />
        </Box>

        <Box mb={32}>
          <Title order={2} size={15} fw={600} mb={16}>
            初期メッセージ
          </Title>
          <Textarea w={520} autosize minRows={10} {...form.getInputProps('lineMessage')} />
        </Box>

        <Group w={520} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="filled"
            radius="xl"
            fz={13}
            fw={600}
            w={120}
            disabled={!isDirty || submitting}
          >
            {submitting ? '処理中' : '保存'}
          </Button>
        </Group>
      </form>
    </Box>
  )
}

export default InitialMessageContainer
